<script setup lang="ts">
import lodashIsNumber from 'lodash-es/isNumber';

import { isDirectSale, isPlatformSale, type SaleItemEntity, type SaleListingItemEntity } from '~/types/sale.type';

type HiddenPriceReason = 'priceOnRequest' | 'priceForGuests' | 'notAvailable';

const HIDDEN_PRICE_REASON_TO_TEXT_KEY: Record<HiddenPriceReason, TranslationKey> = {
    priceOnRequest: 'sdp.DirectSale.directSaleStatus.priceOnRequest',
    priceForGuests: 'sdp.DirectSale.directSaleStatus.priceForGuests',
    notAvailable: 'sale.priceNotAvailable',
};

const HIDDEN_PRICE_REASON_TO_ICON: Record<HiddenPriceReason, string> = {
    priceOnRequest: 'lucide:send',
    priceForGuests: 'lucide:user-lock',
    notAvailable: 'lucide:eye-off',
};

// PROPS
interface Props {
    sale: SaleItemEntity | SaleListingItemEntity;
}

const { sale } = defineProps<Props>();

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const { isLoggedIn } = useAuthUtils();
const { price } = useSalePrice(() => sale);

// DATA
const hiddenPriceReason = computed<HiddenPriceReason | undefined>(() => {
    if (isPlatformSale(sale) && sale.soldOffPlatform) return 'notAvailable';
    if (lodashIsNumber(price.value)) return;
    if (isDirectSale(sale) && sale.priceOnRequest) return 'priceOnRequest';
    if (isDirectSale(sale) && !isLoggedIn.value) return 'priceForGuests';

    return 'notAvailable';
});

const hiddenPriceText = computed(() => hiddenPriceReason.value && tt(HIDDEN_PRICE_REASON_TO_TEXT_KEY[hiddenPriceReason.value]));

const hiddenPriceIcon = computed(() => hiddenPriceReason.value && HIDDEN_PRICE_REASON_TO_ICON[hiddenPriceReason.value]);
</script>

<template>
    <span class="flex items-center gap-3">
        <template v-if="lodashIsNumber(price) && !hiddenPriceReason">
            <span>{{ tn(price, 'currency') }}</span>
            <slot />
        </template>
        <template v-else>
            <SaleNoPricePlaceholder aria-hidden="true" class="whitespace-nowrap" />
            <span class="sr-only">{{ hiddenPriceText }}</span>
            <BaseTooltip :text="hiddenPriceText">
                <span class="flex size-8 items-center justify-center" @click.prevent>
                    <UIcon v-if="hiddenPriceIcon" :name="hiddenPriceIcon" class="text-muted size-5" />
                </span>
            </BaseTooltip>
        </template>
    </span>
</template>
